import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  CheckCircle2,
  CircleHelp,
  Clock3,
  FileStack,
  Gauge,
  RefreshCcw,
  ShieldAlert,
  ShieldCheck,
  Users,
  Workflow,
} from "lucide-react";
import config from "../config/config";
import CockpitShell from "../components/CockpitShell";
import AdminB1Workspace from "../components/admin/AdminB1Workspace";
import AdminErrorBoundary from "../components/admin/AdminErrorBoundary";
import PlatformAccessAdminPanel from "../components/admin/PlatformAccessAdminPanel";
import { Button } from "../components/ui/button";
import { buildAuthHeaders, buildAuthRequestConfig } from "../utils/authSession";

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: BrainCircuit },
  { href: "/dashboard/admin/assessment-ops", label: "Assessment", icon: FileStack },
  { href: "/dashboard/admin/users", label: "Users", icon: Users },
  { href: "/dashboard/security", label: "Security", icon: ShieldCheck },
  { href: "/dashboard/gpt", label: "GPT", icon: Bot },
];

const MODULE_LABELS = {
  reading: "Lesen",
  listening: "Hoeren",
  language_elements: "Sprachbausteine",
  writing: "Schreiben",
  speaking: "Sprechen",
};

const platformSignals = [
  { label: "System", value: "Live", note: "Health und Rollenpfade bleiben sichtbar, aber nachgeordnet." },
  { label: "Ops Fokus", value: "Review", note: "Review Queue bleibt die erste Admin-Arbeitsflaeche." },
  { label: "Scope", value: "B1", note: "B1-Ops fuehren den lokalen Arbeitsmodus." },
];

function noCacheParams(params = {}) {
  return { ...params, _ts: Date.now() };
}

function formatModuleLabel(moduleKey) {
  return MODULE_LABELS[moduleKey] || String(moduleKey || "Unbekannt").replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "Keine Zeitmarke";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Keine Zeitmarke";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function formatRelativeTime(value) {
  if (!value) return "ohne Aktivitaet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "ohne Aktivitaet";
  const diffMinutes = Math.round((date.getTime() - Date.now()) / 60000);
  const absMinutes = Math.abs(diffMinutes);
  const formatter = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });

  if (absMinutes < 60) return formatter.format(diffMinutes, "minute");
  if (absMinutes < 1440) return formatter.format(Math.round(diffMinutes / 60), "hour");
  return formatter.format(Math.round(diffMinutes / 1440), "day");
}

function formatList(items, emptyLabel) {
  return items.length ? items.join(", ") : emptyLabel;
}

function getReviewPressureMeta(pendingCount, fallbackCount, healthOk) {
  if (!healthOk) {
    return {
      tone: "danger",
      label: "Instabil",
      note: "System Health ist nicht sauber. Operative Eingriffe sollten priorisiert werden.",
    };
  }
  if (pendingCount >= 10 || fallbackCount >= 2) {
    return {
      tone: "warning",
      label: "Hoch",
      note: "Queue und Fallback-Last sprechen fuer einen engen Review-Takt.",
    };
  }
  if (pendingCount >= 4) {
    return {
      tone: "active",
      label: "Aktiv",
      note: "Es liegt genug Review-Last an, um die Queue dominant zu halten.",
    };
  }
  return {
    tone: "calm",
    label: "Stabil",
    note: "Review-Druck bleibt kontrollierbar, ohne Folgepfade zu verdraengen.",
  };
}

function getPanelDataState({ loading = false, error = "", hasData = false } = {}) {
  if (error) {
    return hasData
      ? { state: "cached", label: "Cached" }
      : { state: "unavailable", label: "Unavailable" };
  }
  return {
    state: "live",
    label: loading ? "Live Sync" : "Live",
  };
}

function PanelStatusIndicator({ state = "live", label = "Live" }) {
  return (
    <span className={`admin-monitor-status admin-monitor-status--${state}`}>
      <span className="admin-monitor-status__dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function PanelHeader({
  eyebrow,
  title,
  description,
  severity = "calm",
  info,
  state = "live",
  statusLabel = "Live",
  count = null,
  chips = [],
}) {
  return (
    <div className="admin-monitor-panel-header">
      <div className="admin-monitor-panel-header__copy">
        <div className="admin-monitor-panel-header__eyebrow">
          <span className={`admin-monitor-severity-dot is-${severity}`} aria-hidden="true" />
          <span className="dashboard-kicker">{eyebrow}</span>
          {info ? (
            <button type="button" className="admin-monitor-info-button" aria-label={`${title} Info`} title={info}>
              <CircleHelp size={14} strokeWidth={2} />
            </button>
          ) : null}
        </div>
        <h2 className="dashboard-section-title">{title}</h2>
        {description ? <p className="dashboard-section-copy">{description}</p> : null}
        {chips.length ? (
          <div className="admin-monitor-chip-row">
            {chips.map((chip) => (
              <span key={chip.label} className={`admin-monitor-chip${chip.accent ? " is-accent" : ""}`}>
                {chip.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="admin-monitor-panel-header__meta">
        <PanelStatusIndicator state={state} label={statusLabel} />
        {count !== null ? <span className="admin-monitor-count">{count}</span> : null}
      </div>
    </div>
  );
}

function MiniActionCard({ title, meta, description, href, onClick }) {
  return (
    <button type="button" className="admin-monitor-mini-card" data-href={href} onClick={onClick}>
      <div className="admin-monitor-mini-card__head">
        <strong>{title}</strong>
        <ArrowRight size={15} strokeWidth={2} />
      </div>
      <span className="admin-monitor-mini-card__meta">{meta}</span>
      <p>{description}</p>
    </button>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const authHeaders = useMemo(() => buildAuthHeaders(), []);
  const [summary, setSummary] = useState({ total: 0 });
  const [health, setHealth] = useState({ ok: false, name: "Experts4Europe" });
  const [languageHealth, setLanguageHealth] = useState({ summary: null, modules: [] });
  const [languagePool, setLanguagePool] = useState([]);
  const [languageBatches, setLanguageBatches] = useState([]);
  const [languageAttempts, setLanguageAttempts] = useState([]);
  const [languageOpsState, setLanguageOpsState] = useState({ loading: true, error: "", busy: "" });
  const [generatorForm, setGeneratorForm] = useState({ module_key: "reading", count: 3, focus_text: "" });
  const [dashboardState, setDashboardState] = useState({ refreshing: false, error: "" });

  useEffect(() => {
    document.title = "Experts4Europe | Admin Dashboard";
  }, []);

  const loadOverviewSignals = async (headers) => {
    const [healthRes, summaryRes] = await Promise.all([
      axios.get(`${config.API_BASE_URL}/api/health`).catch(() => ({ data: { ok: false, name: "Experts4Europe" } })),
      axios.get(
        `${config.API_BASE_URL}/api/users/summary`,
        buildAuthRequestConfig({
          headers,
          params: noCacheParams({ page: 1, limit: 1 }),
        })
      ).catch(() => ({ data: { total: 0 } })),
    ]);
    setHealth(healthRes.data || { ok: false, name: "Experts4Europe" });
    setSummary(summaryRes.data || { total: 0 });
  };

  const loadLanguageOps = async (headers) => {
    setLanguageOpsState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const [healthRes, poolRes, batchRes, attemptsRes] = await Promise.all([
        axios.get(
          `${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/health`,
          buildAuthRequestConfig({ headers, params: noCacheParams() })
        ),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ status: "review_pending", limit: 12 }),
        }),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/batches`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ limit: 8 }),
        }),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/attempts`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ limit: 8 }),
        }),
      ]);
      setLanguageHealth({
        summary: healthRes.data?.summary || null,
        modules: Array.isArray(healthRes.data?.modules) ? healthRes.data.modules : [],
      });
      setLanguagePool(Array.isArray(poolRes.data?.rows) ? poolRes.data.rows : []);
      setLanguageBatches(Array.isArray(batchRes.data?.rows) ? batchRes.data.rows : []);
      setLanguageAttempts(Array.isArray(attemptsRes.data?.rows) ? attemptsRes.data.rows : []);
      setLanguageOpsState((current) => ({ ...current, loading: false, error: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({
        ...current,
        loading: false,
        error: "Die B1-Pool- und Review-Daten konnten nicht geladen werden.",
      }));
      setLanguageHealth({ summary: null, modules: [] });
      setLanguagePool([]);
      setLanguageBatches([]);
      setLanguageAttempts([]);
    }
  };

  const refreshDashboard = async () => {
    setDashboardState({ refreshing: true, error: "" });
    try {
      await Promise.all([loadOverviewSignals(authHeaders), loadLanguageOps(authHeaders)]);
      setDashboardState({ refreshing: false, error: "" });
    } catch (_err) {
      setDashboardState({
        refreshing: false,
        error: "Die Admin-Signale konnten nicht komplett aktualisiert werden.",
      });
    }
  };

  useEffect(() => {
    refreshDashboard();
  }, []);

  const handleGenerateBatch = async () => {
    setLanguageOpsState((current) => ({ ...current, busy: "generate", error: "" }));
    try {
      await axios.post(
        `${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/generate`,
        generatorForm,
        buildAuthRequestConfig({ headers: authHeaders })
      );
      await loadLanguageOps(authHeaders);
      setGeneratorForm((current) => ({ ...current, focus_text: "" }));
      setLanguageOpsState((current) => ({ ...current, busy: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({ ...current, busy: "", error: "Der Generierungsbatch konnte nicht gestartet werden." }));
    }
  };

  const handleReviewItem = async (itemId, decision) => {
    setLanguageOpsState((current) => ({ ...current, busy: `review:${itemId}`, error: "" }));
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/${itemId}/review`, {
        decision,
        review_note: decision === "approve" ? "Direkt im Admin-Dashboard freigegeben." : "Direkt im Admin-Dashboard abgelehnt.",
      }, buildAuthRequestConfig({ headers: authHeaders }));
      await loadLanguageOps(authHeaders);
      setLanguageOpsState((current) => ({ ...current, busy: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({ ...current, busy: "", error: "Der Review-Status konnte nicht gespeichert werden." }));
    }
  };

  const languageSummary = useMemo(
    () => ({
      pending: languageHealth.summary?.pending_count ?? languagePool.length,
      approved: languageHealth.summary?.approved_count ?? 0,
      rejected: languageHealth.summary?.rejected_count ?? 0,
      fallbackModules: Array.isArray(languageHealth.summary?.fallback_needed_modules)
        ? languageHealth.summary.fallback_needed_modules
        : [],
    }),
    [languageHealth.summary, languagePool.length]
  );

  const attemptsAwaitingReview = useMemo(
    () =>
      languageAttempts.filter((attempt) => {
        const reviewStatus = String(attempt?.review_status || "").toLowerCase();
        const status = String(attempt?.status || "").toLowerCase();
        return !["reviewed", "approved", "completed"].includes(reviewStatus) && !["completed", "approved"].includes(status);
      }).length,
    [languageAttempts]
  );

  const reviewPressure = useMemo(
    () => getReviewPressureMeta(languageSummary.pending, languageSummary.fallbackModules.length, health.ok),
    [health.ok, languageSummary.pending, languageSummary.fallbackModules.length]
  );

  const recentBatch = languageBatches[0] || null;
  const latestAttempt = languageAttempts[0] || null;
  const latestPoolItem = languagePool[0] || null;

  const commandMetrics = useMemo(
    () => [
      {
        label: "Review offen",
        value: languageSummary.pending,
        note: "Offene Items bleiben die erste operative Entscheidungsebene.",
      },
      {
        label: "Freigegeben",
        value: languageSummary.approved,
        note: "Nur freigegebene Basis darf in echte Candidate-Attempts.",
      },
      {
        label: "Fallback-Module",
        value: languageSummary.fallbackModules.length,
        note: formatList(languageSummary.fallbackModules.map(formatModuleLabel), "Keine Module im Fallback."),
      },
      {
        label: "Review-beduerftige Attempts",
        value: attemptsAwaitingReview,
        note: latestAttempt
          ? `Zuletzt aktualisiert ${formatRelativeTime(latestAttempt.updated_at || latestAttempt.created_at)}.`
          : "Noch keine aktiven Attempts im geladenen Zeitraum.",
      },
    ],
    [attemptsAwaitingReview, languageSummary.approved, languageSummary.fallbackModules, languageSummary.pending, latestAttempt]
  );

  const priorityLane = useMemo(
    () => [
      {
        title: "Queue-Druck",
        value: reviewPressure.label,
        note: reviewPressure.note,
        tone: reviewPressure.tone,
        icon: Gauge,
      },
      {
        title: "System Health",
        value: health.ok ? "Live" : "Degraded",
        note: health.ok ? `${health.name || "Experts4Europe"} meldet stabile Basis.` : "Health Endpoint meldet keine saubere Lage.",
        tone: health.ok ? "calm" : "danger",
        icon: health.ok ? CheckCircle2 : ShieldAlert,
      },
      {
        title: "Letzter Batch",
        value: recentBatch ? `#${recentBatch.id}` : "Keiner",
        note: recentBatch
          ? `${formatModuleLabel(recentBatch.module_key)} · ${recentBatch.summary?.review_pending_count ?? 0} offen · ${formatRelativeTime(recentBatch.created_at || recentBatch.updated_at)}`
          : "Noch kein Batch im geladenen Zeitraum.",
        tone: recentBatch?.summary?.review_pending_count ? "warning" : "calm",
        icon: Workflow,
      },
    ],
    [health.name, health.ok, recentBatch, reviewPressure]
  );

  const moduleReadiness = useMemo(
    () =>
      (Array.isArray(languageHealth.modules) ? languageHealth.modules : []).map((module) => {
        const approved = Number(module.approved_count || 0);
        const pending = Number(module.pending_count || 0);
        const weak = Number(module.weak_items || 0);
        const total = approved + pending + weak;
        const readiness = total > 0 ? Math.max(6, Math.round((approved / total) * 100)) : 0;
        return {
          key: module.module_key,
          label: formatModuleLabel(module.module_key),
          status: module.ready ? "Bereit" : approved > 0 ? "Stabilisieren" : "Fallback",
          readiness,
          approved,
          pending,
          weak,
        };
      }),
    [languageHealth.modules]
  );

  const timelineEntries = useMemo(() => {
    const items = [];

    if (latestPoolItem) {
      items.push({
        key: `pool-${latestPoolItem.id}`,
        title: `Review Item #${latestPoolItem.id}`,
        note: `${formatModuleLabel(latestPoolItem.module_key)} wartet auf Freigabe.`,
        meta: `${formatDateTime(latestPoolItem.updated_at || latestPoolItem.created_at)} · Queue`,
      });
    }

    if (recentBatch) {
      items.push({
        key: `batch-${recentBatch.id}`,
        title: `Batch #${recentBatch.id}`,
        note: `${recentBatch.summary?.generated_count ?? 0} erzeugt, ${recentBatch.summary?.review_pending_count ?? 0} noch offen.`,
        meta: `${formatModuleLabel(recentBatch.module_key)} · ${formatDateTime(recentBatch.created_at || recentBatch.updated_at)}`,
      });
    }

    if (latestAttempt) {
      items.push({
        key: `attempt-${latestAttempt.id}`,
        title: `Attempt #${latestAttempt.id}`,
        note: `User ${latestAttempt.userid || "-"} · ${latestAttempt.review_status || "review pending"}`,
        meta: `${latestAttempt.status || "unbekannt"} · ${formatDateTime(latestAttempt.updated_at || latestAttempt.created_at)}`,
      });
    }

    return items;
  }, [latestAttempt, latestPoolItem, recentBatch]);

  const protocolItems = [
    "Review vor Release: ungepruefte Inhalte gehen nie direkt in produktive Candidate-Attempts.",
    "Platform Gate bleibt fuer Incident-Response und kontrollierte Wartungsfenster sofort verfuegbar.",
    "Security und GPT bleiben Eskalationspfade, aber die Review Queue fuehrt die Buehne.",
  ];

  const sharedOpsState = useMemo(
    () =>
      getPanelDataState({
        loading: dashboardState.refreshing,
        error: dashboardState.error,
        hasData: Boolean(summary.total || moduleReadiness.length || timelineEntries.length),
      }),
    [dashboardState.error, dashboardState.refreshing, moduleReadiness.length, summary.total, timelineEntries.length]
  );

  const reviewOpsState = useMemo(
    () =>
      getPanelDataState({
        loading: languageOpsState.loading,
        error: languageOpsState.error,
        hasData: Boolean(languagePool.length || languageBatches.length || languageAttempts.length),
      }),
    [languageAttempts.length, languageBatches.length, languageOpsState.error, languageOpsState.loading, languagePool.length]
  );

  const fallbackChips = useMemo(
    () =>
      languageSummary.fallbackModules.map((module) => ({
        label: formatModuleLabel(module),
        accent: true,
      })),
    [languageSummary.fallbackModules]
  );

  const opsActionCards = useMemo(
    () => [
      {
        title: "Assessment Ops",
        meta: `${languageSummary.pending} offene Reviews`,
        description: "Freigaben, Batch-Steuerung und Candidate-Qualitaet in einer Spur.",
        href: "/dashboard/admin/assessment-ops",
      },
      {
        title: "Security",
        meta: health.ok ? "System live" : "Health degraded",
        description: "Incident-Sicht, harte Signale und Schutzpfade ohne Sucharbeit.",
        href: "/dashboard/security",
      },
      {
        title: "User Management",
        meta: `${summary.total || 0} registriert`,
        description: "Rollen, Zugaenge und Aussteuerung direkt aus dem Admin-Raum heraus.",
        href: "/dashboard/admin/users",
      },
      {
        title: "GPT Control",
        meta: `${attemptsAwaitingReview} aktive Review-Faelle`,
        description: "Agenten- und Prompt-Ops bleiben nah an den operativen Queues.",
        href: "/dashboard/gpt",
      },
    ],
    [attemptsAwaitingReview, health.ok, languageSummary.pending, summary.total]
  );

  return (
    <CockpitShell
      title="AI Operations Room"
      subtitle="Review first bleibt die Regel. B1-Ops, Guardrails und Systemsignale liegen in einer konzentrierten Operations-Struktur statt in mehreren konkurrierenden Dashboards."
      badge="Experts4Europe Admin"
      navItems={navItems}
      wideLayout
      theme="admin"
      shellClassName="admin-dashboard-shell"
      pageClassName="admin-dashboard-page"
      contentClassName="admin-dashboard-content"
      shellLabel="AI Operations Room"
      shellDescription="Ein gemeinsames Ops-Template fuer Review, Audit, Generierung und kompakte Plattformsignale."
      modeLabel="Review First"
      liveLabel="Admin"
      utilityHref={null}
      topSignal="Admin System"
      topContext="AI Operations Room"
      actions={
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/admin/users")}>
            User Ops
          </Button>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/security")}>
            Security
          </Button>
          <Button className="dashboard-action-button" onClick={() => navigate("/dashboard/gpt")}>
            GPT Control
          </Button>
        </>
      }
    >
      <div className="admin-dashboard-scene">
        {dashboardState.error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{dashboardState.error}</div> : null}

        <section className="admin-dashboard-command-band">
          <article className="admin-dashboard-command-board">
            <PanelHeader
              eyebrow="Operations Pulse"
              title="Review-Last, Fallback-Druck und Generierung laufen jetzt als ein klares Command-Layer."
              description="Das Dashboard priorisiert erst die Queue, zeigt dann Stabilitaet und legt Folgepfade wie Security, Users und GPT kompakt daneben statt davor."
              severity={reviewPressure.tone}
              info="Monitor-Technik: einheitlicher Panel-Header mit Severity, Datenzustand und Count statt losem Titelblock."
              state={reviewOpsState.state}
              statusLabel={reviewOpsState.label}
              count={languageSummary.pending}
              chips={fallbackChips.slice(0, 3)}
            />

            <div className="admin-dashboard-command-board__headline">
              <div className="admin-dashboard-command-board__copy">
                <div className="admin-monitor-kpi-strip">
                  {commandMetrics.map((item) => (
                    <article key={item.label} className="admin-monitor-kpi-card">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                      <p>{item.note}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="admin-dashboard-command-board__status">
                <span>Live Fokus</span>
                <strong>{languageSummary.pending > 0 ? `${languageSummary.pending} offene Reviews` : "Queue stabil"}</strong>
                <p>{reviewPressure.note}</p>
              </div>
            </div>

            <div className="admin-dashboard-command-actions">
              <button type="button" className="admin-dashboard-command-link is-primary" onClick={() => navigate("/dashboard/admin/assessment-ops")}>
                <FileStack size={16} strokeWidth={2} />
                <span>Assessment Ops</span>
                <ArrowRight size={15} strokeWidth={2} />
              </button>
              <button type="button" className="admin-dashboard-command-link" onClick={() => navigate("/dashboard/admin/users")}>
                <Users size={16} strokeWidth={2} />
                <span>User Management</span>
              </button>
              <button type="button" className="admin-dashboard-command-link" onClick={refreshDashboard} disabled={dashboardState.refreshing || languageOpsState.loading}>
                <RefreshCcw size={16} strokeWidth={2} className={dashboardState.refreshing ? "is-spinning" : ""} />
                <span>{dashboardState.refreshing ? "Aktualisiere..." : "Daten neu laden"}</span>
              </button>
            </div>
          </article>

          <aside className="admin-dashboard-watchtower">
            <div className="admin-dashboard-watchtower__head">
              <span className="dashboard-kicker">Priority Lane</span>
              <h2 className="dashboard-section-title">Was zuerst auffaellt</h2>
            </div>
            <div className="admin-dashboard-priority-list">
              {priorityLane.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className={`admin-dashboard-priority-card is-${item.tone}`}>
                    <div className="admin-dashboard-priority-card__icon">
                      <Icon size={18} strokeWidth={2} />
                    </div>
                    <div className="admin-dashboard-priority-card__copy">
                      <span>{item.title}</span>
                      <strong>{item.value}</strong>
                      <p>{item.note}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </aside>
        </section>

        <section className="admin-dashboard-overview-grid">
          <article className="admin-dashboard-panel">
            <PanelHeader
              eyebrow="Coverage"
              title="Modulbereitschaft"
              description="Rangliste mit Mikro-Balken statt losem Statistiktext. Genau diese Dichte kommt aus dem Monitor-Muster."
              severity={languageSummary.fallbackModules.length ? "warning" : "calm"}
              info="Monitor-Technik: Rankings mit integrierten Bars machen Prioritaeten in einem Blick lesbar."
              state={reviewOpsState.state}
              statusLabel={reviewOpsState.label}
              count={moduleReadiness.length}
              chips={fallbackChips}
            />
            <div className="admin-dashboard-module-list">
              {moduleReadiness.length ? (
                moduleReadiness.map((module) => (
                  <button
                    key={module.key}
                    type="button"
                    className="admin-dashboard-module-row"
                    onClick={() => navigate("/dashboard/admin/assessment-ops")}
                  >
                    <div className="admin-dashboard-module-row__copy">
                      <div>
                        <strong>{module.label}</strong>
                        <span>{module.status}</span>
                      </div>
                      <p>
                        Freigegeben {module.approved} · Offen {module.pending} · Weak {module.weak}
                      </p>
                    </div>
                    <div className="admin-dashboard-module-row__meter">
                      <div className="admin-dashboard-meter">
                        <span style={{ width: `${module.readiness}%` }} />
                      </div>
                      <strong>{module.readiness}%</strong>
                    </div>
                  </button>
                ))
              ) : (
                <div className="dashboard-empty-tile">Noch keine Modulsignale verfuegbar.</div>
              )}
            </div>
          </article>

          <article className="admin-dashboard-panel">
            <PanelHeader
              eyebrow="Recent Flow"
              title="Letzte Bewegung"
              description="Zeitnahe Ereignisse werden als kompakte Feed-Zeilen statt als Fliesstext gezeigt."
              severity={reviewPressure.tone}
              info="Monitor-Technik: Event-Feed mit klarer Meta-Zeile statt normaler Liste."
              state={reviewOpsState.state}
              statusLabel={reviewOpsState.label}
              count={timelineEntries.length}
            />
            <div className="admin-dashboard-timeline">
              {timelineEntries.length ? (
                timelineEntries.map((item) => (
                  <article key={item.key} className="admin-dashboard-timeline__item">
                    <span className="admin-dashboard-timeline__dot" aria-hidden="true" />
                    <div className="admin-dashboard-timeline__copy">
                      <strong>{item.title}</strong>
                      <p>{item.note}</p>
                      <span>{item.meta}</span>
                    </div>
                  </article>
                ))
              ) : (
                <div className="dashboard-empty-tile">Noch keine frischen Ereignisse im geladenen Zeitraum.</div>
              )}
            </div>
          </article>

          <article className="admin-dashboard-panel">
            <PanelHeader
              eyebrow="Protocol"
              title="Ops-Leitplanken"
              description="Chip- und Inline-Struktur haelt Guardrails dicht sichtbar, ohne die Seite zu verlaengern."
              severity="calm"
              info="Monitor-Technik: kleine Meta-Bausteine und straffe Textblöcke statt Standardkarten."
              state={sharedOpsState.state}
              statusLabel={sharedOpsState.label}
              count={protocolItems.length}
              chips={[
                { label: "Review First", accent: true },
                { label: "No Direct Release" },
                { label: "Access Gate" },
              ]}
            />
            <div className="admin-dashboard-protocol-list">
              {protocolItems.map((item) => (
                <div key={item} className="admin-dashboard-protocol-item">
                  <span className="admin-dashboard-protocol-item__mark" aria-hidden="true" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="admin-dashboard-drill-grid">
          {opsActionCards.map((card) => (
            <MiniActionCard
              key={card.href}
              title={card.title}
              meta={card.meta}
              description={card.description}
              href={card.href}
              onClick={() => navigate(card.href)}
            />
          ))}
        </section>

        <div className="dashboard-body admin-dashboard-workgrid">
          <div className="dashboard-main-stage">
            <section className="dashboard-mission-board admin-dashboard-mission-board">
              <div className="dashboard-section-head admin-dashboard-mission-board__head">
                <PanelHeader
                  eyebrow="Mission Board"
                  title="Review zuerst. Plattformzugang und B1-Ops danach sauber im selben Raum."
                  description='Die Hauptbuehne trennt nicht mehr zwischen "schoenen Karten" und "echten Admin-Tools". Sie behandelt Review, Access Gate und Batch-Steuerung als eine operative Arbeitsflaeche.'
                  severity={reviewPressure.tone}
                  info="Monitor-Technik: eine dominante Board-Fläche mit konsistentem Header statt gemischter Untersektionen."
                  state={reviewOpsState.state}
                  statusLabel={reviewOpsState.label}
                  count={languageSummary.pending}
                />
                <div className="admin-dashboard-inline-signals">
                  <div className="admin-dashboard-inline-signal">
                    <Clock3 size={16} strokeWidth={2} />
                    <span>{languageOpsState.loading ? "Lade Queue..." : `${languageSummary.pending} Queue offen`}</span>
                  </div>
                  <div className="admin-dashboard-inline-signal">
                    <Gauge size={16} strokeWidth={2} />
                    <span>{health.ok ? "System bereit" : "System pruefen"}</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-summary-grid dashboard-summary-grid-admin admin-dashboard-summary-grid">
                <article className="dashboard-summary-card dashboard-summary-card-highlight admin-dashboard-summary-card">
                  <span className="dashboard-summary-label">Review offen</span>
                  <strong>{languageSummary.pending}</strong>
                  <p>Die Queue bleibt dominant und verraet sofort, wie viel manuell entschieden werden muss.</p>
                </article>
                <article className="dashboard-summary-card admin-dashboard-summary-card">
                  <span className="dashboard-summary-label">Freigegebene Basis</span>
                  <strong>{languageSummary.approved}</strong>
                  <p>Nur diese Items duerfen produktive Testlaeufe und Candidate-Attempts bedienen.</p>
                </article>
                <article className="dashboard-summary-card admin-dashboard-summary-card">
                  <span className="dashboard-summary-label">Fallback-Druck</span>
                  <strong>{languageSummary.fallbackModules.length}</strong>
                  <p>{formatList(languageSummary.fallbackModules.map(formatModuleLabel), "Keine Module im Fallback.")}</p>
                </article>
              </div>

              <section className="admin-dashboard-subsection">
                <PanelHeader
                  eyebrow="Access Control"
                  title="Platform Access Gate"
                  description="Globale und rollenspezifische Sperren bleiben operativ sichtbar statt in einem separaten Admin-Formular zu verschwinden."
                  severity={health.ok ? "active" : "danger"}
                  info="Monitor-Technik: eigener Panel-Kopf mit Datenstatus und Count anstelle eines reinen Formularblocks."
                  state={sharedOpsState.state}
                  statusLabel={sharedOpsState.label}
                  count={3}
                  chips={[
                    { label: "Global" },
                    { label: "Applicant" },
                    { label: "Employer" },
                  ]}
                />
                <div className="admin-dashboard-platform-access">
                  <AdminErrorBoundary>
                    <PlatformAccessAdminPanel />
                  </AdminErrorBoundary>
                </div>
              </section>

              <section className="admin-dashboard-subsection">
                <PanelHeader
                  eyebrow="B1 Workbench"
                  title="Review Queue, Generierung und Attempts"
                  description="Der B1-Bereich bleibt transaktional, wirkt aber jetzt wie Teil derselben Command-Oberflaeche."
                  severity={reviewPressure.tone}
                  info="Monitor-Technik: sichtbarer Datenstatus, Count und Metachips direkt ueber dem Arbeitsraum."
                  state={reviewOpsState.state}
                  statusLabel={reviewOpsState.label}
                  count={languageSummary.pending}
                  chips={[
                    { label: `${languageSummary.approved} freigegeben` },
                    { label: `${attemptsAwaitingReview} attempts offen`, accent: attemptsAwaitingReview > 0 },
                  ]}
                />
                <AdminB1Workspace
                  languageHealth={languageHealth}
                  languagePool={languagePool}
                  languageBatches={languageBatches}
                  languageAttempts={languageAttempts}
                  languageOpsState={languageOpsState}
                  generatorForm={generatorForm}
                  setGeneratorForm={setGeneratorForm}
                  handleGenerateBatch={handleGenerateBatch}
                  handleReviewItem={handleReviewItem}
                />
              </section>
            </section>
          </div>

          <aside className="dashboard-secondary-rail admin-dashboard-rail">
            <section className="dashboard-rail-card admin-dashboard-rail-card">
              <PanelHeader
                eyebrow="Signal Rail"
                title="Plattform"
                description="Live-Zustand, Scope und Nutzerlast bleiben als kompakte Monitor-Metriken sichtbar."
                severity={sharedOpsState.state === "unavailable" ? "danger" : "calm"}
                info="Monitor-Technik: sichtbarer Datenzustand plus Count im Rail-Header."
                state={sharedOpsState.state}
                statusLabel={sharedOpsState.label}
                count={platformSignals.length + 2}
              />
              <div className="dashboard-rail-metrics admin-dashboard-rail-metrics">
                {platformSignals.map((item) => (
                  <article key={item.label} className="dashboard-metric-card">
                    <span className="dashboard-summary-label">{item.label}</span>
                    <strong>{item.value}</strong>
                    <p>{item.note}</p>
                  </article>
                ))}
                <article className="dashboard-metric-card">
                  <span className="dashboard-summary-label">Registrierte Nutzer</span>
                  <strong>{summary.total || 0}</strong>
                  <p>Quelle `/api/users/summary`, sichtbar aber bewusst leiser als die Review-Flaeche.</p>
                </article>
                <article className="dashboard-metric-card">
                  <span className="dashboard-summary-label">Health</span>
                  <strong>{health.ok ? `${health.name || "Experts4Europe"} OK` : "Degraded"}</strong>
                  <p>Systemsignal bleibt kompakt, aber ohne seinen Warnwert zu verlieren.</p>
                </article>
              </div>
            </section>

            <section className="dashboard-rail-card admin-dashboard-rail-card">
              <PanelHeader
                eyebrow="Quick Paths"
                title="Direkt erreichbar"
                description="Klickbare Mini-Panels ersetzen simple Textbuttons und geben jedem Pfad Gewicht."
                severity="active"
                info="Monitor-Technik: klickbare Mini-Cards mit Hover-Rahmen statt nackter Listenbuttons."
                state="live"
                statusLabel="Live"
                count={4}
              />
              <div className="dashboard-action-stack admin-dashboard-quick-stack">
                <button type="button" className="dashboard-stack-button is-primary" onClick={() => navigate("/dashboard/admin/users")}>
                  <Users size={16} strokeWidth={2} />
                  <span>Users</span>
                </button>
                <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/admin/assessment-ops")}>
                  <FileStack size={16} strokeWidth={2} />
                  <span>Assessment</span>
                </button>
                <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/security")}>
                  <ShieldCheck size={16} strokeWidth={2} />
                  <span>Security</span>
                </button>
                <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/gpt")}>
                  <Bot size={16} strokeWidth={2} />
                  <span>GPT</span>
                </button>
              </div>
            </section>

            <section className="dashboard-rail-card admin-dashboard-rail-card">
              <PanelHeader
                eyebrow="Control Notes"
                title="Jetzt beobachten"
                description="Dichte Mini-Cards statt Fliesstext zeigen, wo Admin-Aufmerksamkeit gerade hin muss."
                severity={reviewPressure.tone}
                info="Monitor-Technik: kompakte Notes mit starker Wert-Hierarchie."
                state={reviewOpsState.state}
                statusLabel={reviewOpsState.label}
                count={3}
              />
              <div className="admin-dashboard-watch-notes">
                <article className="admin-dashboard-watch-note">
                  <span>Queue</span>
                  <strong>{languageSummary.pending}</strong>
                  <p>Offene Reviews sind die schnellste Wahrheit ueber Admin-Last.</p>
                </article>
                <article className="admin-dashboard-watch-note">
                  <span>Batch</span>
                  <strong>{recentBatch ? `#${recentBatch.id}` : "Keiner"}</strong>
                  <p>
                    {recentBatch
                      ? `${formatModuleLabel(recentBatch.module_key)} · ${recentBatch.summary?.generated_count ?? 0} erzeugt`
                      : "Noch kein frischer Batch im geladenen Zeitraum."}
                  </p>
                </article>
                <article className="admin-dashboard-watch-note">
                  <span>Letzter Attempt</span>
                  <strong>{latestAttempt ? `#${latestAttempt.id}` : "Keiner"}</strong>
                  <p>{latestAttempt ? formatDateTime(latestAttempt.updated_at || latestAttempt.created_at) : "Keine Attempt-Aktivitaet sichtbar."}</p>
                </article>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </CockpitShell>
  );
};

export default AdminDashboard;
